import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, leadsAPI } from '../../services/api';

function UserListing() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLeads, setUserLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const response = await usersAPI.getAllUsers(params);
      setUsers(response.data.users || response.data);
      setFilteredUsers(response.data.users || response.data);
      if (response.data.pagination) {
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

   useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: 'all',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const deleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.deleteUser(id);
        setUsers(users.filter((user) => user._id !== id));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const viewUserLeads = async (user) => {
    setSelectedUser(user);
    setLoadingLeads(true);
    setShowLeadsModal(true);
    setStatusFilter('all');
    try {
      const response = await leadsAPI.getAssignedLeads(user._id, { limit: 100 }); // Get more leads for modal
      setUserLeads(response.data.leads || response.data);
      setFilteredLeads(response.data.leads || response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch user leads');
      setUserLeads([]);
      setFilteredLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    if (status === 'all') {
      setFilteredLeads(userLeads);
    } else {
      setFilteredLeads(userLeads.filter(lead => lead.progressStatus === status));
    }
  };

  const closeLeadsModal = () => {
    setShowLeadsModal(false);
    setSelectedUser(null);
    setUserLeads([]);
    setFilteredLeads([]);
    setStatusFilter('all');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      case 'Cold Lead': return 'bg-gray-100 text-gray-800';
      case 'Prospecting': return 'bg-blue-100 text-blue-800';
      case 'Qualified': return 'bg-emerald-100 text-emerald-800';
      case 'Demo Scheduled / Done': return 'bg-purple-100 text-purple-800';
      case 'Evaluation / Pilot': return 'bg-indigo-100 text-indigo-800';
      case 'Decision': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
      <h2 className="text-2xl sm:text-3xl font-bold mb-4">User Listing</h2>
      {error && <p className="text-red-500 mb-4 text-sm sm:text-base">{error}</p>}
      
      {/* Search and Filter */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Search users by username..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            className="p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              handleFilterChange('sortBy', sortBy);
              handleFilterChange('sortOrder', sortOrder);
            }}
            className="p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="username-asc">Username A-Z</option>
            <option value="username-desc">Username Z-A</option>
          </select>
        </div>
        
        <div className="flex justify-between items-center">
          <button
            onClick={clearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded-xl hover:bg-gray-600 transition"
          >
            Clear Filters
          </button>
          <div className="text-sm text-gray-600">
            Showing {filteredUsers.length} of {pagination.totalCount} users
          </div>
        </div>
      </div>
      
      {loading && <p className="text-center py-8">Loading users...</p>}
      
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3 text-left">Username</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Specialization</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user._id} className="border-b hover:bg-gray-50">
                <td className="p-3 font-medium">{user.username}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex gap-1 flex-wrap">
                    {user.isOnboardingSpecialist && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        Onboarding
                      </span>
                    )}
                    {user.isChannelPartner && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                        Channel Partner
                      </span>
                    )}
                    {!user.isOnboardingSpecialist && !user.isChannelPartner && (
                      <span className="text-gray-500 text-xs">None</span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => viewUserLeads(user)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition text-sm whitespace-nowrap"
                    >
                      View Leads
                    </button>
                    <button
                      onClick={() => deleteUser(user._id)}
                      className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden space-y-4">
        {filteredUsers.map((user) => (
          <div key={user._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
              <div className="flex-1">
                <h3 className="font-bold text-lg text-gray-900">{user.username}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-1">Specialization:</p>
              <div className="flex gap-2 flex-wrap">
                {user.isOnboardingSpecialist && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                    Onboarding
                  </span>
                )}
                {user.isChannelPartner && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                    Channel Partner
                  </span>
                )}
                {!user.isOnboardingSpecialist && !user.isChannelPartner && (
                  <span className="text-gray-500 text-xs">None</span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => viewUserLeads(user)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition text-sm font-medium w-full sm:w-auto"
              >
                View Leads
              </button>
              <button
                onClick={() => deleteUser(user._id)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition text-sm font-medium w-full sm:w-auto"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-6">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrev}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const pageNum = Math.max(1, pagination.currentPage - 2) + i;
            if (pageNum > pagination.totalPages) return null;
            
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-2 rounded-lg border ${
                  pageNum === pagination.currentPage
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNext}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* User Leads Modal */}
      {showLeadsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 sm:p-6 border-b gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  Leads: {selectedUser.username} ({filteredLeads.length})
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mt-1">
                  Total Value: ${filteredLeads.reduce((sum, lead) => sum + (lead.pipelineValue || 0), 0).toFixed(2)}
                </p>
              </div>
              <button
                onClick={closeLeadsModal}
                className="text-gray-500 hover:text-gray-700 p-2 self-end sm:self-auto -mt-2 sm:mt-0"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Status Filter */}
            <div className="p-4 sm:p-6 border-b">
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status ({userLeads.length})</option>
                <option value="Prospecting">Prospecting ({userLeads.filter(l => l.progressStatus === 'Prospecting').length})</option>
                <option value="Qualified">Qualified ({userLeads.filter(l => l.progressStatus === 'Qualified').length})</option>
                <option value="Demo Scheduled / Done">Demo Scheduled / Done ({userLeads.filter(l => l.progressStatus === 'Demo Scheduled / Done').length})</option>
                <option value="Evaluation / Pilot">Evaluation / Pilot ({userLeads.filter(l => l.progressStatus === 'Evaluation / Pilot').length})</option>
                <option value="Decision">Decision ({userLeads.filter(l => l.progressStatus === 'Decision').length})</option>
                <option value="Closed Won">Closed Won ({userLeads.filter(l => l.progressStatus === 'Closed Won').length})</option>
                <option value="Closed Lost">Closed Lost ({userLeads.filter(l => l.progressStatus === 'Closed Lost').length})</option>
              </select>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {loadingLeads ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading leads...</p>
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No leads assigned</h3>
                  <p className="mt-2 text-gray-500">This user has no leads assigned to them.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                  {filteredLeads.map((lead) => (
                    <div key={lead._id} className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3 gap-2">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate flex-1">{lead.schoolName}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(lead.progressStatus)}`}>
                          {lead.progressStatus}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                         <div className="flex justify-between gap-2">
                          <span className="font-medium">Person Name:</span>
                          <span className="truncate text-right">{lead.personName}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Email:</span>
                          <span className="truncate text-right">{lead.email}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Address:</span>
                          <span className="truncate text-right">{lead.address}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Phone:</span>
                          <span className="text-right">{lead.phoneNumber}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Source:</span>
                          <span className="text-right">{lead.source}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Expected Value:</span>
                          <span className="font-semibold text-right">${(lead.expectedValue || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Probability:</span>
                          <span className="font-semibold text-right">{lead.probability || 0}%</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Latest Rating:</span>
                          <span className="font-semibold text-right">
                            {lead.ratingHistory && lead.ratingHistory.length > 0 
                              ? lead.ratingHistory[lead.ratingHistory.length - 1].rating 
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2 border-t pt-2">
                          <span className="font-bold">Pipeline Value:</span>
                          <span className="font-bold text-green-600 text-right">${(lead.pipelineValue || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Created:</span>
                          <span className="text-right">{new Date(lead.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">Updated:</span>
                          <span className="text-right">{new Date(lead.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserListing;